import { AttachFile, ChatOutlined, ErrorOutline } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchWhatsAppBatch, fetchWhatsAppBatches, fetchWhatsAppFileUrl } from "api/whatsapp";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getMrtBodyRowSx, getMrtTableThemeProps } from "theme/mrtTableTheme";
import { getApiError } from "utils/apiError";
import { formatDisplayDateTime } from "utils/dateFormat";

const BatchStatusChip = ({ status }) => {
  const map = {
    SUCCESS: { color: "success", variant: "outlined" },
    FAILED: { color: "error", variant: "filled" },
    PARTIAL: { color: "warning", variant: "filled" },
  };
  const { color, variant } = map[status] || { color: "default", variant: "outlined" };
  return <Chip size="small" label={status || "UNKNOWN"} color={color} variant={variant} />;
};

const MetaField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {label}
    </Typography>
    <Typography variant="body2" className="break-words">
      {value || "—"}
    </Typography>
  </Box>
);

const WhatsAppFileChips = ({ batchId, names }) => {
  const [openingIndex, setOpeningIndex] = useState(null);
  if (!names?.length) return null;

  const openFile = async (index) => {
    if (openingIndex !== null) return;
    setOpeningIndex(index);
    try {
      const url = await fetchWhatsAppFileUrl(batchId, index);
      if (!url) throw new Error("empty link");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(getApiError(err, "Could not open this file. Please try again."));
    } finally {
      setOpeningIndex(null);
    }
  };

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
      {names.map((name, index) => {
        const opening = openingIndex === index;
        return (
          <Tooltip key={`${name}-${index}`} title={`Open ${name}`}>
            <span>
              <Chip
                size="small"
                icon={opening ? <CircularProgress size={12} /> : <AttachFile />}
                label={opening ? `Opening ${name}…` : name}
                variant="outlined"
                onClick={() => openFile(index)}
                sx={{ cursor: "pointer" }}
              />
            </span>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

const WhatsAppBatchPanel = ({ batchId }) => {
  const { data: batch, isFetching } = useQuery(
    ["whatsapp-batch", batchId],
    () => fetchWhatsAppBatch(batchId),
    { enabled: !!batchId, refetchOnWindowFocus: false }
  );

  if (isFetching && !batch) {
    return (
      <Box className="flex items-center justify-center py-8">
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!batch) return null;

  const failed = (batch.recipients || []).filter((row) => row.status === "FAILED");

  return (
    <Box sx={{ p: 2, bgcolor: "background.default" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} flexWrap="wrap">
        <MetaField label="Sent by" value={batch.sentByName || batch.sentByEmail} />
        <MetaField label="Source" value={batch.source} />
        <MetaField label="Sent at" value={formatDisplayDateTime(batch.sentAt)} />
      </Stack>
      {(batch.tags || []).length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {batch.tags.map((tag) => (
              <Chip key={tag} size="small" label={tag} variant="outlined" />
            ))}
          </Stack>
        </>
      )}
      {(batch.mediaNames || []).length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            FILES
          </Typography>
          <WhatsAppFileChips batchId={batchId} names={batch.mediaNames} />
        </>
      )}
      {failed.length > 0 && (
        <Box className="flex gap-2 p-3 mt-3 rounded-md bg-red-50 text-red-700 border border-red-100">
          <ErrorOutline fontSize="small" />
          <Typography variant="body2">
            {failed.length} recipient{failed.length > 1 ? "s" : ""} did not get this WhatsApp.
          </Typography>
        </Box>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        RECIPIENTS ({batch.totalCount})
      </Typography>
      <Box className="divide-y divide-slate-100 max-h-48 overflow-y-auto rounded border border-app-border bg-app-surface mt-1">
        {(batch.recipients || []).map((row) => (
          <Box key={`${row.id}-${row.phone}`} className="flex items-center gap-3 px-3 py-2">
            <Box className="min-w-0 flex-1">
              <Typography variant="body2" className="truncate">
                {row.recipientName || row.phone}
              </Typography>
              <Typography variant="caption" color="text.secondary" className="truncate block">
                {row.phone}
                {row.mediaName ? ` · ${row.mediaName}` : ""}
              </Typography>
              {row.errorMessage && (
                <Typography variant="caption" color="error" className="truncate block">
                  {row.errorMessage}
                </Typography>
              )}
            </Box>
            <BatchStatusChip status={row.status} />
          </Box>
        ))}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        MESSAGE
      </Typography>
      <Box className="mt-2 p-3 rounded-lg border border-app-border bg-app-surface whitespace-pre-wrap text-sm">
        {batch.body || "No message captured."}
      </Box>
    </Box>
  );
};

const WhatsAppLogs = () => {
  const [filters, setFilters] = useState({ pageIndex: 0, pageSize: 20, search: "" });
  const { data, isFetching } = useQuery(
    ["whatsapp-batches", filters],
    () =>
      fetchWhatsAppBatches({
        page: filters.pageIndex,
        size: filters.pageSize,
        search: filters.search,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );
  const batches = data?.content || [];
  const totalItems = data?.totalItems || 0;

  const columns = useMemo(
    () => [
      {
        accessorKey: "subject",
        header: "Message",
        size: 280,
        Cell: ({ row }) => (
          <div className="min-w-0">
            <Typography variant="body2" fontWeight={600} className="truncate">
              {row.original.subject || row.original.body || "(No text)"}
            </Typography>
            {row.original.source && (
              <Typography variant="caption" color="text.secondary">
                {row.original.source}
              </Typography>
            )}
          </div>
        ),
      },
      {
        accessorKey: "batchStatus",
        header: "Status",
        size: 110,
        Cell: ({ row }) => <BatchStatusChip status={row.original.batchStatus} />,
      },
      {
        accessorKey: "totalCount",
        header: "Recipients",
        size: 110,
        Cell: ({ cell }) => <Chip size="small" variant="outlined" label={cell.getValue() ?? 0} />,
      },
      {
        accessorKey: "sentByName",
        header: "Sent by",
        size: 160,
        Cell: ({ row }) => (
          <span className="text-sm truncate block">
            {row.original.sentByName || row.original.sentByEmail || "—"}
          </span>
        ),
      },
      {
        accessorKey: "sentAt",
        header: "Sent",
        size: 160,
        Cell: ({ cell }) => (
          <span className="text-sm whitespace-nowrap">
            {formatDisplayDateTime(cell.getValue())}
          </span>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: batches,
    getRowId: (row) => String(row.batchId),
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableStickyHeader: true,
    enableExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalItems,
    pageCount: Math.ceil(totalItems / filters.pageSize) || 0,
    onPaginationChange: (updater) => {
      setFilters((prev) => {
        const pagination =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, pageIndex: pagination.pageIndex, pageSize: pagination.pageSize };
      });
    },
    onGlobalFilterChange: (value) => {
      setFilters((prev) => ({ ...prev, search: value ?? "", pageIndex: 0 }));
    },
    initialState: { density: "compact", showGlobalFilter: true },
    muiPaginationProps: { rowsPerPageOptions: [10, 20, 50, 100] },
    paginationDisplayMode: "pages",
    state: {
      isLoading: isFetching,
      showProgressBars: isFetching,
      globalFilter: filters.search || "",
      pagination: { pageIndex: filters.pageIndex, pageSize: filters.pageSize },
    },
    ...getMrtTableThemeProps({ maxHeight: "calc(100vh - 360px)" }),
    muiTableBodyRowProps: ({ row }) => ({ sx: getMrtBodyRowSx(row) }),
    renderDetailPanel: ({ row }) => <WhatsAppBatchPanel batchId={row.original.batchId} />,
    renderEmptyRowsFallback: () => (
      <Box className="flex flex-col items-center justify-center py-16 text-center">
        <ChatOutlined className="text-slate-300 mb-2" sx={{ fontSize: 48 }} />
        <Typography fontWeight={600}>No WhatsApp messages found</Typography>
        <Typography variant="body2" color="text.secondary">
          {isFetching ? "Loading…" : "Messages sent from the dashboard show up here."}
        </Typography>
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default WhatsAppLogs;
